import { useState } from "react"
import styled from "styled-components"

import { Banner, spacing, Text } from "@minuthq/meatball-ui-react"

import { EmptyState } from "src/components/EmptyState"
import { ParadiseDetailsHeading } from "src/components/Paradise/ParadiseDetailsHeading"
import { ParadiseDetailsLayout } from "src/components/Paradise/ParadiseDetailsLayout"
import { ParadiseHomeActions } from "src/components/Paradise/ParadiseHomes/ParadiseHomeDetails/ParadiseHomeActions"
import { ParadiseHomeCompassSandbox } from "src/components/Paradise/ParadiseHomes/ParadiseHomeDetails/ParadiseHomeCompassSandbox/ParadiseHomeCompassSandbox"
import { ParadiseHomeLogs } from "src/components/Paradise/ParadiseHomes/ParadiseHomeDetails/ParadiseHomeLogs/ParadiseHomeLogs"
import { ParadiseHomeMembers } from "src/components/Paradise/ParadiseHomes/ParadiseHomeDetails/ParadiseHomeMembers/ParadiseHomeMembers"
import { ParadiseHomeOccupancyGraph } from "src/components/Paradise/ParadiseHomes/ParadiseHomeDetails/ParadiseHomeOccupancyGraph/ParadiseHomeOccupancyGraph"
import { ParadiseHomeOverview } from "src/components/Paradise/ParadiseHomes/ParadiseHomeDetails/ParadiseHomeOverview/ParadiseHomeOverview"
import { ParadiseHomeRestoreDialog } from "src/components/Paradise/ParadiseHomes/ParadiseHomeDetails/ParadiseHomeRestoreDialog"
import { LoadingWrapper } from "src/components/Paradise/sharedStyles"
import { useAddRecentlyViewedPage } from "src/components/RecentlyViewed/useAddRecentlyViewedPage"
import { useFetchParadiseHome } from "src/data/paradise/paradiseHomes/queries/paradiseHomeQueries"
import { langKeys } from "src/i18n/langKeys"
import { useTranslate } from "src/i18n/useTranslate"
import { Routes } from "src/router/routes"
import { MButton } from "src/ui/Button/MButton"
import Svg404 from "src/ui/icons/404.svg"
import { MSkeleton } from "src/ui/MSkeleton/MSkeleton"
import { TabPanel } from "src/ui/Tabs/TabPanel"
import { Tabs } from "src/ui/Tabs/Tabs"

import { ParadiseHomeDevices } from "./ParadiseHomeDevices/ParadiseHomeDevices"
import { ParadiseHomeEvents } from "./ParadiseHomeEvents/ParadiseHomeEvents"
import { ParadiseHomeGuests } from "./ParadiseHomeGuests"

export function ParadiseHomeDetails({ homeId }: { homeId: string }) {
  const { t } = useTranslate()
  const [showRestoreDialog, setShowRestoreDialog] = useState(false)

  const fetchHome = useFetchParadiseHome({
    homeId: homeId,
  })

  useAddRecentlyViewedPage({
    title: fetchHome.data?.name,
    enitytId: fetchHome.data?.id,
    type: "Home",
    enabled: fetchHome.isSuccess,
  })

  if (fetchHome.isError) {
    return (
      <EmptyState
        title={t(langKeys.failed_general_error_title)}
        body={t(langKeys.failed_general_error_body)}
        icon={<Svg404 />}
      />
    )
  }

  if (fetchHome.isLoading) {
    return (
      <LoadingWrapper>
        <MSkeleton height={40} width="30ch" />
        <MSkeleton height={20} width="40ch" />
      </LoadingWrapper>
    )
  }

  const home = fetchHome.data

  return (
    <>
      <ParadiseDetailsLayout
        meta={<ParadiseDetailsHeading>{home.name}</ParadiseDetailsHeading>}
        banner={
          home.deleted && (
            <Banner type="error" fullWidth>
              <BannerWrapper>
                <Text>Home is deleted.</Text>
                <MButton
                  variant="minimal"
                  onClick={() => setShowRestoreDialog(true)}
                >
                  Restore home
                </MButton>
              </BannerWrapper>
            </Banner>
          )
        }
        actions={<ParadiseHomeActions home={home} />}
        tabs={[
          {
            id: "compass_sandbox",
            label: "Compass sandbox",
            component: (
              <ParadiseHomeCompassSandbox
                homeId={homeId}
                orgId={home.owner.id}
              />
            ),
          },
        ]}
        left={<ParadiseHomeOverview home={home} />}
        right={
          <Tabs
            labels={[
              {
                id: "general",
                label: "General",
              },
              {
                id: "home_logs",
                label: "Home logs",
              },
              {
                id: "home_events",
                label: "Home events",
              },
              {
                id: "guests",
                label: "Guests",
              },
              {
                id: "occupancy_graph",
                label: "Occupancy graph",
              },
            ]}
          >
            <TabPanel>
              <GenerelGrid>
                <ParadiseHomeMembers orgId={home.owner.id} homeId={homeId} />
                <ParadiseHomeDevices homeId={homeId} />
              </GenerelGrid>
            </TabPanel>

            <TabPanel>
              <ParadiseHomeLogs homeId={homeId} />
            </TabPanel>

            <TabPanel>
              <ParadiseHomeEvents homeId={homeId} home={home} />
            </TabPanel>
            <TabPanel>
              <ParadiseHomeGuests homeId={homeId} home={home} />
            </TabPanel>
            <TabPanel>
              <ParadiseHomeOccupancyGraph homeId={homeId} />
            </TabPanel>
          </Tabs>
        }
        breadcrumbs={[
          {
            label: "Homes",
            to: Routes.ParadiseHomes.location(),
          },
          {
            label: home.id,
            to: Routes.ParadiseHome.location(home.id),
          },
        ]}
        breadcrumbCopyValue={home.id}
      />

      <ParadiseHomeRestoreDialog
        open={showRestoreDialog}
        homeName={home.name}
        homeId={home.id}
        onClose={() => setShowRestoreDialog(false)}
      />
    </>
  )
}

const BannerWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${spacing.S};
`

const GenerelGrid = styled.div`
  display: grid;
  gap: ${spacing.M};
`
